import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, X, CheckCircle2, List, BarChart3, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { API, statusColors } from "./utils";
import Gantt from "frappe-gantt";
import "@/styles/frappe-gantt.css";

// Gantt chart component
function GanttChart({ milestones, project }) {
  const ganttRef = useRef(null);
  const ganttInstance = useRef(null);

  useEffect(() => {
    if (!ganttRef.current || milestones.length === 0) return;

    // Clear previous instance
    ganttRef.current.innerHTML = '';

    // Convert milestones to Gantt tasks
    const tasks = milestones.map((ms, idx) => {
      const startDate = ms.planned_date || project.start_date;
      // Estimate end date as 7 days after start if not specified
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);
      
      return {
        id: ms.id,
        name: ms.name,
        start: startDate,
        end: endDate.toISOString().split('T')[0],
        progress: ms.completion_percent || 0,
        dependencies: '',
        custom_class: ms.status === 'Completed' ? 'bar-completed' : 
                     ms.status === 'In Progress' ? 'bar-in-progress' :
                     ms.status === 'Delayed' ? 'bar-delayed' : ''
      };
    });

    if (tasks.length > 0) {
      try {
        ganttInstance.current = new Gantt(ganttRef.current, tasks, {
          view_mode: 'Week',
          date_format: 'YYYY-MM-DD',
          language: 'en',
          custom_popup_html: (task) => {
            const milestone = milestones.find(m => m.id === task.id);
            return `
              <div class="gantt-popup-title">${task.name}</div>
              <div class="gantt-popup-subtitle">
                <p>Status: ${milestone?.status || 'Pending'}</p>
                <p>Progress: ${task.progress}%</p>
                <p>Planned: ${task.start}</p>
              </div>
            `;
          }
        });
      } catch (error) {
        console.error("Failed to initialize Gantt chart:", error);
      }
    }

    return () => {
      if (ganttRef.current) {
        ganttRef.current.innerHTML = '';
      }
    };
  }, [milestones, project]);

  if (milestones.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <BarChart3 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
        <p>Add milestones to see the Gantt chart</p>
      </div>
    );
  }

  return (
    <div className="gantt-wrapper">
      <style>{`
        .gantt-wrapper {
          overflow-x: auto;
          background: #ffffff;
          border-radius: 8px;
          padding: 16px;
          min-height: 300px;
        }
        .gantt-wrapper svg.gantt {
          background: #ffffff !important;
        }
        .gantt-wrapper .gantt .bar-wrapper .bar {
          fill: #3b82f6 !important;
          stroke: none !important;
        }
        .gantt-wrapper .gantt .bar-wrapper .bar-progress {
          fill: #1d4ed8 !important;
        }
        .gantt-wrapper .gantt .bar-wrapper .bar-label {
          fill: #1e293b !important;
          font-weight: 500 !important;
        }
        .gantt-wrapper .gantt .bar-wrapper.bar-completed .bar {
          fill: #10b981 !important;
        }
        .gantt-wrapper .gantt .bar-wrapper.bar-completed .bar-progress {
          fill: #059669 !important;
        }
        .gantt-wrapper .gantt .bar-wrapper.bar-in-progress .bar {
          fill: #3b82f6 !important;
        }
        .gantt-wrapper .gantt .bar-wrapper.bar-delayed .bar {
          fill: #ef4444 !important;
        }
        .gantt-wrapper .gantt .bar-wrapper.bar-delayed .bar-progress {
          fill: #dc2626 !important;
        }
        .gantt-wrapper .gantt .grid-header {
          fill: #f1f5f9 !important;
        }
        .gantt-wrapper .gantt .grid-row {
          fill: #ffffff !important;
        }
        .gantt-wrapper .gantt .grid-row:nth-child(even) {
          fill: #f8fafc !important;
        }
        .gantt-wrapper .gantt .tick {
          stroke: #e2e8f0 !important;
        }
        .gantt-wrapper .gantt text {
          fill: #334155 !important;
        }
        .gantt-wrapper .gantt .lower-text, 
        .gantt-wrapper .gantt .upper-text {
          fill: #64748b !important;
          font-size: 12px !important;
        }
        .gantt-wrapper .gantt .today-highlight {
          fill: #fef3c7 !important;
        }
        .gantt-wrapper .gantt .handle {
          fill: #0ea5e9 !important;
        }
        .gantt-popup-title {
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 8px;
        }
        .gantt-popup-subtitle {
          font-size: 12px;
          color: #64748b;
        }
        .gantt-popup-subtitle p {
          margin: 2px 0;
        }
      `}</style>
      <div ref={ganttRef}></div>
    </div>
  );
}

function TimelineContent({ projectId, project, milestones, onRefresh, canWrite, token }) {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [formData, setFormData] = useState({ name: "", description: "", planned_date: "", status: "Pending", completion_percent: 0 });

  const resetForm = () => {
    setFormData({ name: "", description: "", planned_date: "", status: "Pending", completion_percent: 0 });
    setEditingMilestone(null);
    setShowForm(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects/${projectId}/milestones`, null, {
        params: { name: formData.name, description: formData.description, planned_date: formData.planned_date },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Milestone created");
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error("Failed to create milestone");
    }
  };

  const handleEditMilestone = (milestone) => {
    setFormData({
      name: milestone.name || "",
      description: milestone.description || "",
      planned_date: milestone.planned_date || "",
      status: milestone.status || "Pending",
      completion_percent: milestone.completion_percent || 0
    });
    setEditingMilestone(milestone);
    setShowForm(true);
    setViewMode("list");
  };

  const handleUpdateMilestone = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/projects/${projectId}/milestones/${editingMilestone.id}`, null, {
        params: formData,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Milestone updated");
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error("Failed to update milestone");
    }
  };

  const handleQuickStatusUpdate = async (milestone, newStatus, newPercent) => {
    try {
      await axios.put(`${API}/projects/${projectId}/milestones/${milestone.id}`, null, {
        params: {
          name: milestone.name,
          description: milestone.description,
          planned_date: milestone.planned_date,
          status: newStatus,
          completion_percent: newPercent
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Milestone marked as ${newStatus}`);
      onRefresh();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if (!window.confirm("Delete this milestone?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/milestones/${milestoneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Milestone deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete milestone");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Timeline: {project.start_date} → {project.target_date}</p>
        <div className="flex items-center gap-2">
          {canWrite && !showForm && (
            <Button size="sm" onClick={() => { setShowForm(true); setViewMode("list"); }} className="bg-brillien-600 hover:bg-brillien-700" data-testid="add-milestone-btn">
              <Plus className="h-4 w-4 mr-1" />Add Milestone
            </Button>
          )}
        </div>
      </div>

      {/* View Toggle */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="list" className="flex items-center gap-2" data-testid="timeline-list-view">
            <List className="h-4 w-4" />
            List View
          </TabsTrigger>
          <TabsTrigger value="gantt" className="flex items-center gap-2" data-testid="timeline-gantt-view">
            <BarChart3 className="h-4 w-4" />
            Gantt Chart
          </TabsTrigger>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate(`/projects/${projectId}/gantt`)}
            className="ml-auto"
            data-testid="full-page-gantt-btn"
          >
            <Maximize2 className="h-4 w-4 mr-1" />
            Full Page View
          </Button>
        </TabsList>

        {/* List View */}
        <TabsContent value="list">
          {showForm && (
            <Card className="p-4 border-brillien-200 bg-brillien-50 mb-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold">{editingMilestone ? "Edit Milestone" : "Add Milestone"}</h4>
                <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
              </div>
              <form onSubmit={editingMilestone ? handleUpdateMilestone : handleCreate} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Name *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Planned Date *</Label>
                    <Input type="date" value={formData.planned_date} onChange={(e) => setFormData({...formData, planned_date: e.target.value})} required />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} />
                </div>
                {editingMilestone && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Status</Label>
                      <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                          <SelectItem value="Delayed">Delayed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Completion %</Label>
                      <Input type="number" min="0" max="100" value={formData.completion_percent} onChange={(e) => setFormData({...formData, completion_percent: parseInt(e.target.value) || 0})} />
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="submit" size="sm" className="bg-brillien-600 hover:bg-brillien-700">
                    {editingMilestone ? "Update" : "Save"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={resetForm}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {milestones.length === 0 && !showForm ? (
            <p className="text-slate-500 text-center py-8">No milestones defined</p>
          ) : !showForm && (
            <div className="space-y-3">
              {milestones.map(ms => (
                <Card key={ms.id} className="p-4 border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-slate-900">{ms.name}</h5>
                      <p className="text-sm text-slate-500">Planned: {ms.planned_date}</p>
                      {ms.description && <p className="text-xs text-slate-400 mt-1">{ms.description}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={statusColors[ms.status]}>{ms.status}</Badge>
                      <span className="text-sm font-medium">{ms.completion_percent}%</span>
                      {canWrite && (
                        <div className="flex gap-1">
                          {ms.status !== "Completed" && (
                            <Button variant="ghost" size="sm" onClick={() => handleQuickStatusUpdate(ms, "Completed", 100)} className="text-green-600" title="Mark Complete">
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEditMilestone(ms)} className="text-blue-600" title="Edit">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteMilestone(ms.id)} className="text-red-600" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <Progress value={ms.completion_percent} className="h-1.5 mt-3" />
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Gantt Chart View */}
        <TabsContent value="gantt">
          <Card className="p-4 border-slate-200">
            <GanttChart milestones={milestones} project={project} />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export { TimelineContent };
